/**
 * The agents workspace API (FOR-326): Cody, the Reviewer and the Sweeper,
 * under /api/agents. Owner only on the API side (403 owner_required for
 * anyone else); the UI hides the workspace for non-owners too.
 *
 * Shapes follow the API's AgentRunPresenter and the setup controller. There
 * are no money figures anywhere in this module, by design.
 */

import { apiClient } from "@/lib/api"

/* ── Runs ──────────────────────────────────────────────────────────────── */

export type RunPhase =
  | "queued"
  | "working"
  | "reviewing"
  | "waiting"
  | "revising"
  | "merging"
  | "watching"
  | "shipped"
  | "reverted"
  | "rejected"
  | "diagnosed"
  | "failed"

/** The `outcome` filter of GET /agents/runs. */
export type RunOutcomeFilter = "open" | "merged" | "shipped" | "reverted" | "rejected" | "failed" | "diagnosed"

export type Zone = "green" | "red"
export type Verdict = "approve" | "concerns" | "changes"
export type WaitingReason = "red" | "calibration" | "level" | "github" | "ci" | "watch" | string

export interface RunAgentRef {
  id: number
  slug: string
  name: string
  kind: string
  avatar: string | null
}

export interface RunTaskRef {
  id: number
  key: string | null
  title: string
  board_key: string | null
  state: string | null
  url: string | null
}

export interface DeliveryFile {
  path: string
  previous_filename?: string | null
  status?: string | null
  add: number
  del: number
  binary?: boolean
}

/** A PR as Forge fetched it from GitHub (never from the packet). */
export interface DeliveryPr {
  repository: string
  number: number
  url: string
  state?: string | null
  merged?: boolean | null
  draft?: boolean | null
  base_ref?: string | null
  head_ref?: string | null
  head_sha?: string | null
  additions?: number | null
  deletions?: number | null
  changed_files?: number | null
  files?: DeliveryFile[] | null
  merge_sha?: string | null
  merge_commit_sha?: string | null
  merged_at?: string | null
  node_id?: string | null
}

export interface DeliveryCi {
  state: "passed" | "failed" | "running" | "none"
  checks?: unknown
  since?: string | null
  checked_at?: string | null
  error?: string | null
}

export interface DeliveryTests {
  /** False when the command failed; the counts are null when it printed none (a build, a crash). */
  ok?: boolean | null
  passed: number | null
  failed: number | null
  skipped: number | null
  errors?: number | null
  suite: string | null
  duration: number | string | null
  exit_code?: number | null
  timed_out?: boolean | null
  output_tail?: string | null
}

export interface DeliveryReviewer {
  verdict: Verdict
  summary: string | null
  notes: string[] | null
  run_id: number | null
  reviewed_sha?: Record<string, string> | null
}

export interface DeliveryMerge {
  state: "pending" | "merged" | "partial" | "failed"
  sha: string | null
  at: string | null
  error: string | null
  merged?: number[] | null
}

export interface RevertPr {
  number: number
  url: string
  state: "open" | "merged" | string
  repository?: string
  error?: string | null
}

export interface DeliveryWatch {
  state: "watching" | "reverting" | "passed" | "reverted" | "needs_attention" | "failed"
  until: string | null
  result: string | null
  revert_prs?: RevertPr[] | null
  revert_started_at?: string | null
  /** needs_attention only: new errors Forge could not tie to the change. */
  candidates?: { id: string | number; title: string; events: number | null; url: string | null }[] | null
}

export interface Delivery {
  plan?: string[] | null
  summary?: string | null
  prs?: DeliveryPr[] | null
  /** What the packet named, when Forge could not verify it or refused it. */
  claimed_prs?: { repository: string; number: number; head_sha: string | null }[] | null
  ci?: DeliveryCi | null
  tests?: DeliveryTests | null
  reviewer?: DeliveryReviewer | null
  merge?: DeliveryMerge | null
  watch?: DeliveryWatch | null
  waiting_because?: WaitingReason[] | null
  review_run_id?: number | null
  rounds?: number | null
  revising?: boolean | null
  diagnosis?: string | null
  closed?: { state: string; at: string | null; error: string | null } | null
}

/** What the runner reported; every key optional. Counts only, never money. */
export interface RunUsage {
  input_tokens?: number
  output_tokens?: number
  turns?: number
  minutes?: number
  model?: string
  tool_calls?: number
  [key: string]: unknown
}

export interface RunSummary {
  id: number
  agent: RunAgentRef | null
  task: RunTaskRef | null
  repository: string | null
  product: string | null
  /** The repo's autonomy level (0–3) when the run was queued. */
  level: number | null
  status: string
  phase: RunPhase
  zone: Zone | null
  zone_reasons: string[]
  trigger: string | null
  run_kind: string | null
  parent_run_id: number | null
  waiting_because: WaitingReason[]
  delivery: Delivery | null
  usage: RunUsage | null
  error: string | null
  not_before: string | null
  paused_for_takeover: boolean | null
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  last_heartbeat_at: string | null
}

export interface RunApproval {
  decision: "approved" | "rejected" | "changes_requested" | string
  note: string | null
  decided_at: string | null
  decided_by: { id: number; name: string } | null
}

export interface RunDetail extends RunSummary {
  brief: string | null
  packet: Record<string, unknown> | null
  report: Record<string, unknown> | null
  session_ref: string | null
  claimed_by: string | null
  budget: Record<string, number> | null
  approvals: RunApproval[]
  children: RunSummary[]
  takeover: string | null
}

export interface RunEvent {
  seq: number
  type: string
  payload: Record<string, unknown> | null
  at: string | null
}

export interface Paginated<T> {
  data: T[]
  current_page: number
  per_page: number
  total: number
  last_page: number
}

/* ── Sweeps ────────────────────────────────────────────────────────────── */

/** One sweep list entry. Event counts are the last 24 hours. */
export interface SweepItem {
  title: string
  culprit: string | null
  events: number | null
  users: number | null
  url: string | null
  reason: string | null
  task: { id: number; key: string } | null
  run_id: number | null
  sentry_id?: string | null
}

export interface SweepReport {
  errors?: number | null
  grouped?: number | null
  filed?: SweepItem[] | null
  to_cody?: SweepItem[] | null
  dismissed?: SweepItem[] | null
  /** What the per-sweep caps held back, with the reason. */
  skipped?: SweepItem[] | null
  feedback?: SweepItem[] | null
  problems?: SweepItem[] | null
}

export interface SweepSummary extends RunSummary {
  report: SweepReport | null
}

/* ── Overview, usage, sessions ─────────────────────────────────────────── */

export interface UsagePayload {
  date: string
  agents: { slug: string; name: string; runs_today: number; cap: number | null }[]
  totals_today: { input_tokens: number; output_tokens: number; turns: number; minutes: number }
  seat: { five_hour_used_pct?: number | null; weekly_used_pct?: number | null; resets_at?: string | null } | null
  seat_reported_at: string | null
  seat_runner_id: string | null
}

export interface AgentBudget {
  max_minutes?: number
  max_turns?: number
  runs_per_day?: number
}

/** disabled: no runs and no assignments, not even by an owner, until re-enabled. */
export type AgentStatus = "active" | "paused" | "disabled"

export interface OverviewAgent {
  id: number
  slug: string
  name: string
  kind: string
  engine: string | null
  status: "active" | "paused" | "disabled" | string
  /** Set while status is disabled: why it is off (FOR-336). */
  disabled_reason?: string | null
  disabled_at?: string | null
  health: string | null
  role_title: string | null
  avatar: string | null
  color: string | null
  user: { id: number; name: string; email: string; boards?: string[] } | null
  budget: AgentBudget | null
  last_run_at: string | null
  current_run: RunSummary | null
  queued: number
  today: { runs: number; cap: number | null; prs?: number; waiting: number; shipped: number; reverted: number; failed: number }
  next_run: { at: string; what: string } | null
}

export interface Overview {
  agents: OverviewAgent[]
  usage: UsagePayload
  waiting: number
  calibration: { started_at: string | null; ends_at: string | null; active: boolean }
  started_at: string | null
}

export interface SessionRun extends RunSummary {
  session_ref: string | null
  claimed_by: string | null
  takeover: string | null
}

/** A tmux session a runner reported; `name` is run-<id> for a run's own session. */
export interface BoxSession {
  name: string
  user: string | null
  project: string | null
  started_at: string | null
  attach: string | null
}

export interface RunnerSessions {
  runner_id: string
  os_user: string | null
  capabilities: string[]
  last_seen_at: string | null
  sessions: BoxSession[]
}

/* ── Repos and setup ───────────────────────────────────────────────────── */

export interface AgentRepo {
  id: number
  repository: string
  product: string
  boards: string[]
  default_branch: string
  autonomy_level: number
  max_level: number
  test_command: string | null
  enabled: boolean
  merge_on_approve: boolean
  /** Cody takes the board app's feedback tasks on its own (owner's standing say-so). */
  auto_pickup?: boolean
  sentry_projects: string[]
  red_paths: string[]
  max_lines: number
  preflight: { ok: boolean; at: string | null; error: string | null; runner_id?: string | null } | null
  updated_at: string | null
}

export type RepoPatch = Partial<Pick<AgentRepo, "autonomy_level" | "enabled" | "test_command" | "merge_on_approve" | "auto_pickup" | "red_paths" | "max_lines" | "sentry_projects">>

export type SecretKey = "claude_oauth_token" | "sentry_token" | "github_merge_token"

export interface SecretSummary {
  key: SecretKey
  set: boolean
  last4: string | null
  set_at: string | null
  verified_at: string | null
  verify_error: string | null
}

export type SetupStatus = "done" | "todo" | "warn"

export type SetupAction =
  | { kind: "paste"; secret: SecretKey; secret_status: SecretSummary; command?: string | null }
  | { kind: "copy"; text: string }
  | { kind: "button"; method: "POST"; endpoint: string; label: string }
  | { kind: "link"; url: string; label: string }

export interface SetupStep {
  key: string
  title: string
  status: SetupStatus
  detail: string | null
  action: SetupAction | null
  required: boolean
  repos?: { repository: string; preflight: AgentRepo["preflight"] }[]
  notes?: { repository: string; merge_on_approve: boolean; note: string }[]
}

export interface AgentSettings {
  calibration_started_at: string | null
  calibration_days: number
  calibration_ends_at: string | null
  digest_hour: number
  sweep_time: string
  started_at: string | null
  sweeper_handoff_enabled: boolean
  sweeper_handoff_owner_id: number | null
  sweep_caps: { filed_per_project: number; to_cody: number }
  cody_runner_release_sha: string | null
  timezone: string
}

export type SettingsPatch = Partial<Pick<AgentSettings, "sweep_time" | "digest_hour" | "calibration_days" | "sweeper_handoff_enabled">> & {
  sweep_caps?: Partial<AgentSettings["sweep_caps"]>
}

export interface SetupPayload {
  steps: SetupStep[]
  blocking: string[]
  secrets: SecretSummary[]
  settings: AgentSettings
}

/** The Cody runner's machine key, shown once (ROOT-SETUP.sh asks for it). */
export interface RunnerKey {
  key: string
  name: string
  scopes: string[]
  install_path: string
  created_at: string | null
}

export interface StartPauseResult {
  agents: { slug: string; name: string; kind: string; status: string }[]
  started_at: string | null
}

/* ── Calls ─────────────────────────────────────────────────────────────── */

export interface RunListParams {
  agent?: string
  product?: string
  phase?: string
  outcome?: RunOutcomeFilter
  zone?: Zone
  task_id?: number
  per_page?: number
  page?: number
}

// apiClient returns the response body, so a {data} envelope needs one unwrap
// and a paginator none.
/** A paginator that also carries a summary block (/agents/shipped). */
export type ShippedPage = Paginated<RunSummary> & { week?: { shipped: number; watching: number; reverted: number } }

export const agentsApi = {
  overview: () => apiClient.get<{ data: Overview }>("/agents/overview").then((r) => r.data),
  usage: () => apiClient.get<{ data: UsagePayload }>("/agents/usage").then((r) => r.data),
  sessions: () => apiClient.get<{ data: { runs: SessionRun[]; runners: RunnerSessions[] } }>("/agents/sessions").then((r) => r.data),

  runs: (params?: RunListParams) => apiClient.get<Paginated<RunSummary>>("/agents/runs", { params }),
  run: (id: number, params?: { page?: number; per_page?: number }) =>
    apiClient.get<{ data: RunDetail; events: Paginated<RunEvent> }>(`/agents/runs/${id}`, { params }),
  approve: (id: number, note?: string | null) =>
    apiClient.post<{ data: RunDetail }>(`/agents/runs/${id}/approve`, note ? { note } : {}).then((r) => r.data),
  reject: (id: number, note: string) => apiClient.post<{ data: RunDetail }>(`/agents/runs/${id}/reject`, { note }).then((r) => r.data),
  requestChanges: (id: number, note: string) =>
    apiClient.post<{ data: RunDetail }>(`/agents/runs/${id}/request-changes`, { note }).then((r) => r.data),
  reReview: (id: number) => apiClient.post<{ data: RunDetail }>(`/agents/runs/${id}/re-review`).then((r) => r.data),
  takeover: (id: number) => apiClient.post<{ data: RunDetail; command: string }>(`/agents/runs/${id}/takeover`),
  resume: (id: number) => apiClient.post<{ data: RunDetail; warning?: "session_live" | string }>(`/agents/runs/${id}/resume`),

  review: (params?: { per_page?: number; page?: number }) => apiClient.get<Paginated<RunSummary>>("/agents/review", { params }),
  shipped: (params?: { per_page?: number; page?: number }) => apiClient.get<ShippedPage>("/agents/shipped", { params }),
  sweeps: (params?: { per_page?: number; page?: number }) => apiClient.get<Paginated<SweepSummary>>("/agents/sweeps", { params }),

  repos: () => apiClient.get<{ data: AgentRepo[] }>("/agents/repos").then((r) => r.data),
  updateRepo: (id: number, patch: RepoPatch) => apiClient.patch<{ data: AgentRepo }>(`/agents/repos/${id}`, patch).then((r) => r.data),
  updateAgent: (slug: string, body: { status?: AgentStatus; disabled_reason?: string | null; budget?: AgentBudget }) =>
    apiClient.patch<{ data: { id: number; slug: string; name: string; status: string; disabled_reason: string | null; budget: AgentBudget | null } }>(`/agents/${slug}`, body).then((r) => r.data),

  start: () => apiClient.post<{ data: StartPauseResult }>("/agents/start").then((r) => r.data),
  pause: () => apiClient.post<{ data: StartPauseResult }>("/agents/pause").then((r) => r.data),
  updateSettings: (patch: SettingsPatch) => apiClient.patch<{ data: AgentSettings }>("/agents/settings", patch).then((r) => r.data),

  setup: () => apiClient.get<{ data: SetupPayload }>("/agents/setup").then((r) => r.data),
  putSecret: (key: SecretKey, value: string) => apiClient.put<{ data: SecretSummary }>(`/agents/secrets/${key}`, { value }).then((r) => r.data),
  deleteSecret: (key: SecretKey) => apiClient.delete<{ data: SecretSummary }>(`/agents/secrets/${key}`).then((r) => r.data),
  mintRunnerKey: () => apiClient.post<{ data: RunnerKey }>("/agents/setup/runner-key").then((r) => r.data),
}
